import { CSSProperties, memo, useMemo } from 'react';

import { FlexAligner, Typography } from '@scaleaq/scaleui';
import { GensetState } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { NoWrapTypography } from '@/pages/bargeControl/styles.ts';
import { colors, Icon, responsive } from '@/utils';

const iconSize = '3.5rem';
const iconStyles: CSSProperties = {
  color: colors.icon.action[100],
  width: iconSize,
  height: iconSize,
};

type StatProp = {
  state: GensetState;
  powerP: number;
};
Object.entries(GensetState).reduce(
  (acc, [key, value]) => {
    acc[value] = key as keyof typeof GensetState;
    return acc;
  },
  {} as Record<string, keyof typeof GensetState>
);

export const GeneratorState = memo(({ state, powerP }: StatProp) => {
  const { t } = useTranslation(['hybridPower']);
  const renderStateComponent = useMemo(() => {
    switch (state) {
      case GensetState.Running:
        return <Icon.CircleCheck size='large' style={{ ...iconStyles }} />;
      case GensetState.Stopped:
        return <Icon.CircleStop size='large' style={{ ...iconStyles }} />;
      case GensetState.Starting:
        return <Icon.CirclePlay size='large' style={{ ...iconStyles }} />;
      case GensetState.Delivering:
        return <Icon.Zap size='large' style={{ ...iconStyles }} />;
      case GensetState.Synced:
        return <Icon.History size='large' style={{ ...iconStyles }} />;
      case GensetState.CoolDown:
        return <Icon.Snowflake size='large' style={{ ...iconStyles }} />;
      case GensetState.Invalid:
      default:
        return (
          <Icon.CircleAlert
            size='large'
            style={{ ...iconStyles, color: colors.icon.critical[100] }}
          />
        );
    }
  }, [state]);

  return (
    <FlexAligner alignment='center' fullHeight>
      <FlexAligner direction='row' spaceBetween alignment='center' gap={responsive.spacing.small}>
        <FlexAligner
          fullWidth={false}
          direction='row'
          gap={responsive.spacing.medium}
          alignment='center'
        >
          {renderStateComponent}
          <Typography type='heading_medium'>
            {t(`hybridPower:generator.generatorState.${state as GensetState}`)}
          </Typography>
        </FlexAligner>
        <NoWrapTypography type='heading_large'>
          {`${Math.round(Number(powerP ?? 0))} KW`}
        </NoWrapTypography>
      </FlexAligner>
    </FlexAligner>
  );
});
